import { classnames } from 'helpers/utils';
import styles from './horizontal-tabs.module.scss';

export type TabItem<T extends string = string> = {
  id: T;
  label: string;
  disabled?: boolean;
  // Tooltip, e.g. why the tab is disabled.
  title?: string;
};

type HorizontalTabsProps<T extends string = string> = {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (tabId: T) => void;
  className?: string;
  size?: 'small' | 'medium';
};

const HorizontalTabs = <T extends string = string>({
  tabs,
  activeTab,
  onChange,
  className = '',
  size = 'small',
}: HorizontalTabsProps<T>) => {
  const handleTabClick = (tabId: T, disabled?: boolean) => {
    if (disabled) return;
    onChange(tabId);
  };

  return (
    <div className={classnames(styles.container, styles[size], className)}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={classnames(styles.tabButton, {
            [styles.active]: activeTab === tab.id,
            [styles.disabled]: !!tab.disabled,
          })}
          onClick={() => handleTabClick(tab.id, tab.disabled)}
          disabled={tab.disabled}
          title={tab.title}
          aria-selected={activeTab === tab.id}
          role="tab"
        >
          <span
            className={classnames(
              styles.tabLabel,
              activeTab === tab.id
                ? 'text__body__semi__bold__small__gray700'
                : 'text__body__semi__bold__small__gray600',
            )}
          >
            {tab.label}
          </span>
        </button>
      ))}
    </div>
  );
};

export { HorizontalTabs };
